import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Btn } from '../components/Btn.tsx';
import { AuthSplitLayout, AuthInfoChip } from '../components/AuthSplitLayout.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { readSeededReason, type DenyReason } from '../lib/auth/denyReason.ts';
import { buildIdentityLogoutUrl } from '../lib/auth/identityLogout.ts';

// Terminal screen for an admin the API refused (403). Reached instead of a
// silent SSO bounce: the portal session is deliberately still alive, so this
// page can name who is signed in and offer a sign-out that actually ends the
// central session too.
//
// This is the ONLY place the refusal wording exists. The API sends a machine
// reason code and never renders prose (see the throw in
// apps/api/src/middleware/adminAuth.ts), so the portal owns the wording outright
// and can change it without touching the API (spec S10).
//
// access_check_unavailable MUST stay distinct from the other two — different
// heading, different tone, different action. Telling someone they lack a group
// when the truth is that our identity service was unreachable produces a support
// ticket and an Okta administrator hunting for a group that was never the
// problem. It is also the only reason for which retrying is meaningful.
const COPY: Record<DenyReason, { title: string; body: string; note: string }> = {
  not_in_group: {
    title: 'Access denied',
    body: "Your Okta account doesn't have access to this portal.",
    note: 'Ask your IT administrator to add you to the group that grants access to the compliance admin portal.',
  },
  not_provisioned: {
    title: 'Access denied',
    body: "You don't have access to this portal.",
    note: 'Ask your IT administrator to request access, then sign in again.',
  },
  access_check_unavailable: {
    title: "We couldn't check your access",
    body: "This isn't a problem with your account — we couldn't reach the service that confirms your access. Please try again shortly.",
    note: 'If this keeps happening, contact your IT administrator and mention that the portal access check is unavailable.',
  },
};

export function AdminNoAccess() {
  const { accessDenied, user, session, loading, reload, signOut } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);
  const [retries, setRetries] = useState(0);

  // Callers seed the reason through history state so the first paint after a
  // redirect is already correct, rather than showing one reason until the
  // provider's own /admin/me call lands and then swapping. Only a recognised
  // code counts as a seed, so unrelated router state cannot read as a refusal.
  // The context wins as soon as it has an answer — it is the freshly-checked one.
  const seed = readSeededReason(location.state);
  const denied = accessDenied ?? (seed ? { reason: seed } : null);

  // The seed has a short life by design: once the context has answered, drop it
  // from history. Otherwise it outlives its truth — a retry that resolves to a
  // 401 clears `accessDenied` and signs the user out, and a seed left in place
  // would keep asserting a refusal against someone who is no longer signed in.
  useEffect(() => {
    if (accessDenied && seed) navigate(location.pathname, { replace: true });
  }, [accessDenied, seed, location.pathname, navigate]);

  // Access granted after all (a retry succeeded, or a group was added and the
  // session refreshed). Leaving them on a refusal page would be worse than the
  // bounce loop. Mirrors AdminLogin's "never strand an authenticated admin".
  //
  // `!accessDenied` is redundant today — the provider never sets both — but it
  // is what makes this safe to read on its own: AdminShell redirects here on
  // `accessDenied`, so without it the two files would ping-pong if that
  // invariant were ever broken in a third file.
  useEffect(() => {
    if (user && !accessDenied) navigate('/admin', { replace: true });
  }, [user, accessDenied, navigate]);

  // Sign-out from here has to do the full central hand-off, exactly as
  // AdminShell does. A portal-only sign-out would leave the l2u-identity and
  // Okta sessions alive, so "sign in again" would silently return the same
  // refused identity — reintroducing the loop from the other end.
  // The `reason` param (added by #178) lets the central page explain the
  // refusal on its own domain. Its vocabulary is a CLOSED enum whose only
  // member is `portal_access_denied`, and that copy reads "You don't have
  // access to that portal" — true for the two real denials, and FALSE for
  // `access_check_unavailable`, which means our own check did not complete.
  // So that reason is passed through as undefined: an absent reason renders
  // the plain signed-out page rather than a confident claim we cannot support.
  const handleSignOut = useCallback(async () => {
    navigate('/admin/signed-out', { replace: true });
    await signOut();
    const centralLogout = buildIdentityLogoutUrl(
      import.meta.env.VITE_IDENTITY_LOGOUT_URL,
      `${window.location.origin}/admin/signed-out`,
      // Mirrors the render fallback below exactly: no denial in hand means the
      // check never completed, which is the one case that must NOT claim denial.
      (denied?.reason ?? 'access_check_unavailable') === 'access_check_unavailable'
        ? undefined
        : 'portal_access_denied',
    );
    if (centralLogout) window.location.href = centralLogout;
  }, [navigate, signOut, denied]);

  const onRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await reload();
    } finally {
      setRetrying(false);
      setRetries((n) => n + 1);
    }
  }, [reload]);

  // Renders a spinner rather than the page while the navigate effect above
  // runs, so a successful retry never flashes "access denied" on its way out.
  if (user && !accessDenied) return <CenteredSpinner />;
  // Landed here directly (a refresh, or a bookmark) with no seeded reason: wait
  // for /admin/me rather than asserting a reason and then swapping.
  if (!denied && loading) return <CenteredSpinner />;
  // Nothing was refused and there is no session: a bookmark, a hand-typed URL,
  // or a retry that resolved to a 401 and signed the user out. Telling someone
  // who simply is not signed in that they lack access is the same harm this
  // screen exists to prevent, pointed the other way — it sends them to their IT
  // administrator over a problem that does not exist. /admin/login auto-starts
  // SSO, which is what a genuinely signed-out person needs.
  if (!denied && !session) return <Navigate to="/admin/login" replace />;

  // Past the guards, either there is a denial or there is a live session with no
  // verdict — a reload that failed on the network, say. That is an access check
  // which did not finish, not a refusal, and saying so is both true and the one
  // reason that blames nobody.
  const reason = denied?.reason ?? 'access_check_unavailable';
  const copy = COPY[reason];
  const email = session?.user.email ?? null;
  const transient = reason === 'access_check_unavailable';

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">{copy.title}</h2>
          <p className="text-sm text-slate-500">{copy.body}</p>
        </div>

        {email && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-600">
            You&rsquo;re signed in as <span className="font-medium text-slate-800">{email}</span>.
          </div>
        )}

        {transient && retries > 0 && !retrying && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            We still couldn&rsquo;t check your access. Please try again in a few minutes.
          </div>
        )}

        <div className="flex flex-col gap-3">
          {transient && (
            <Btn
              type="button"
              loading={retrying}
              onClick={() => void onRetry()}
              className="w-full"
              style={{ borderRadius: 10, height: 48 }}
            >
              Try again
            </Btn>
          )}
          <Btn
            type="button"
            variant={transient ? 'secondary' : 'primary'}
            onClick={() => void handleSignOut()}
            className="w-full"
            style={{ borderRadius: 10, height: 48 }}
          >
            Sign out
          </Btn>
          <AuthInfoChip tone="neutral">{copy.note}</AuthInfoChip>
        </div>
      </div>
    </AuthSplitLayout>
  );
}
